import { TrendingUp } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  formatCurrency,
  formatMonth,
  formatMonthTick,
  formatNumber,
  niceMax,
  tooltipStyle,
  xAxisTickProps,
} from './chart-utils';

interface ChartData {
  date?: string;
  month?: string;
  トークン数: number;
  リクエスト数: number;
  コスト: number;
}

interface DailyChartsProps {
  data: ChartData[];
}

/**
 * 日別グラフセクション（トークン、料金、リクエスト）
 */
export function DailyCharts({ data }: DailyChartsProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
      {/* 日別トークン使用量 */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h2 className="text-lg font-semibold text-foreground mb-4">日別トークン使用量</h2>
        <div className="h-56 md:h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9ca3af" fontSize={10} {...xAxisTickProps} />
              <YAxis
                stroke="#9ca3af"
                fontSize={10}
                tickFormatter={formatNumber}
                domain={[0, niceMax]}
              />
              <Tooltip
                {...tooltipStyle}
                formatter={(value) => [formatNumber(Number(value)), 'トークン数']}
              />
              <Bar dataKey="トークン数" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 日別利用料金 */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h2 className="text-lg font-semibold text-foreground mb-4">日別利用料金</h2>
        <div className="h-56 md:h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9ca3af" fontSize={10} {...xAxisTickProps} />
              <YAxis
                stroke="#9ca3af"
                fontSize={10}
                tickFormatter={(v) => `$${v}`}
                domain={[0, niceMax]}
              />
              <Tooltip
                {...tooltipStyle}
                formatter={(value) => [formatCurrency(Number(value)), '利用料金']}
              />
              <Bar dataKey="コスト" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 日別リクエスト数 */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h2 className="text-lg font-semibold text-foreground mb-4">日別リクエスト数</h2>
        <div className="h-56 md:h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9ca3af" fontSize={10} {...xAxisTickProps} />
              <YAxis stroke="#9ca3af" fontSize={10} domain={[0, niceMax]} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="リクエスト数" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

interface MonthlyTrendChartsProps {
  data: ChartData[];
}

/**
 * 月別推移グラフセクション（トークン、料金、リクエスト）
 */
export function MonthlyTrendCharts({ data }: MonthlyTrendChartsProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
      {/* 月別トークン推移 */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          月別トークン推移
        </h2>
        <div className="h-56 md:h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="month"
                stroke="#9ca3af"
                fontSize={10}
                tickFormatter={formatMonthTick}
                {...xAxisTickProps}
              />
              <YAxis
                stroke="#9ca3af"
                fontSize={10}
                tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                domain={[0, niceMax]}
              />
              <Tooltip
                {...tooltipStyle}
                labelFormatter={(label) => formatMonth(String(label))}
                formatter={(value) => [formatNumber(Number(value)), 'トークン数']}
              />
              <Bar dataKey="トークン数" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 月別料金推移 */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          月別料金推移
        </h2>
        <div className="h-56 md:h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="month"
                stroke="#9ca3af"
                fontSize={10}
                tickFormatter={formatMonthTick}
                {...xAxisTickProps}
              />
              <YAxis
                stroke="#9ca3af"
                fontSize={10}
                tickFormatter={(v) => `$${v}`}
                domain={[0, niceMax]}
              />
              <Tooltip
                {...tooltipStyle}
                labelFormatter={(label) => formatMonth(String(label))}
                formatter={(value) => [formatCurrency(Number(value)), '利用料金']}
              />
              <Bar dataKey="コスト" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 月別リクエスト推移 */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          月別リクエスト推移
        </h2>
        <div className="h-56 md:h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="month"
                stroke="#9ca3af"
                fontSize={10}
                tickFormatter={formatMonthTick}
                {...xAxisTickProps}
              />
              <YAxis stroke="#9ca3af" fontSize={10} domain={[0, niceMax]} />
              <Tooltip
                {...tooltipStyle}
                labelFormatter={(label) => formatMonth(String(label))}
                formatter={(value) => [formatNumber(Number(value)), 'リクエスト数']}
              />
              <Bar dataKey="リクエスト数" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
